import { useState, useCallback } from 'react';
import clsx from 'clsx';
import { Bookmark, BookmarkActionViewMode } from '../../../types/api';
import {
  getSelectionPositionClass,
  getBatchSectionPositionClass,
} from '../../../utils/selectionUtils';
import { useBookmarkPageContent } from '../../../contexts/BookmarkPageContext';
import type { AnchorPosition } from '../../ui/Popover';
import BookmarkActions from '../../BookmarkActions';
import BookmarkCheckbox from './BookmarkCheckbox';
import BookmarkTitle from './BookmarkTitle';
import BookmarkMeta from './BookmarkMeta';
import BookmarkDescription from './BookmarkDescription';
import BookmarkThumbnail from './BookmarkThumbnail';
import BookmarkNotesTags from './BookmarkNotesTags';
import { useBookmarkInteractions } from './hooks/useBookmarkInteractions';
import { useDomainExtraction } from './hooks/useDomainExtraction';
import { useReadingTime } from './hooks/useReadingTime';
import { useThumbnailUrl } from './hooks/useThumbnailUrl';

interface BookmarkRowProps extends Bookmark {
  condensedView?: boolean;
  showThumbnails?: boolean;
  viewMode?: BookmarkActionViewMode;
  currentFolderId?: string;
  profileMode?: boolean;
}

function BookmarkRow({
  condensedView = false,
  showThumbnails = true,
  viewMode = 'home',
  currentFolderId,
  profileMode = false,
  ...bookmark
}: BookmarkRowProps) {
  const {
    id,
    title,
    url,
    description,
    hash,
    reading_time,
    time,
    author,
    notes,
    tags,
    og_image,
    private_source,
  } = bookmark;

  // Custom hooks for logic extraction
  const {
    isSelected,
    isBatchMode,
    isLoggedIn,
    isMobile,
    showActions,
    isKeyboardNavigable,
    showAriaPressed,
    handleClick,
    handleDoubleClick,
    handleKeyDown,
    ariaLabel,
    selectionContext,
  } = useBookmarkInteractions({
    bookmarkId: id,
    bookmarkTitle: title,
    bookmarkUrl: url,
    profileMode,
  });

  // Get bookmarks array for selection position class
  // Reuse selectionContext from useBookmarkInteractions hook
  const bookmarkPage = useBookmarkPageContent();
  const index =
    bookmarkPage?.bookmarks.findIndex((b) => String(b.id) === String(id)) ?? -1;
  const selectionPositionClass =
    bookmarkPage && selectionContext && isBatchMode && isSelected && index >= 0
      ? selectionContext.sectionBatchTarget
        ? getBatchSectionPositionClass(index, bookmarkPage.bookmarks.length)
        : getSelectionPositionClass(
            id,
            index,
            bookmarkPage.bookmarks,
            selectionContext.selectedBookmarkIds,
          )
      : null;

  // Check if this bookmark is currently being read (active state)
  // Coerce to string so we match even if one side is number (e.g. from URL or API)
  const isActive =
    bookmarkPage?.activeBookmark != null &&
    String(bookmarkPage.activeBookmark.id) === String(id);

  const domain = useDomainExtraction({ url });
  const source = private_source || domain;
  const readTimeText = useReadingTime({ reading_time });
  const thumbnailUrl = useThumbnailUrl({
    bookmarkId: id,
    hash,
    profileMode,
    ogImage: og_image,
  });

  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsAnchorPosition, setActionsAnchorPosition] =
    useState<AnchorPosition | null>(null);

  const handleActionsOpenChange = useCallback((open: boolean) => {
    setActionsOpen(open);
    // Clear anchor position when popover closes, and when opening via button, so next open uses the trigger
    setActionsAnchorPosition(null);
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!showActions) return;
      if (e.altKey || e.ctrlKey) return;
      const target = e.target as Element;
      if (target.closest('a')) return;
      e.preventDefault();
      setActionsAnchorPosition({ x: e.clientX, y: e.clientY });
      setActionsOpen(true);
    },
    [showActions],
  );

  return (
    <label
      className={clsx(
        'bookmark-item',
        condensedView && 'condensed',
        profileMode && 'profile-mode',
        // Selection states
        isBatchMode && 'batch-mode',
        isBatchMode && isSelected && 'selected',
        // Selection position class for connected styling
        selectionPositionClass,
        // Active state when bookmark is being read
        !isBatchMode && isActive && 'active',
        // Mobile edit mode opacity
        isMobile && isBatchMode && !isSelected && 'opacity-60',
        // Mobile class for hover behavior
        isMobile && 'mobile',
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={isKeyboardNavigable ? 0 : -1}
      aria-pressed={showAriaPressed ? isSelected : undefined}
      aria-label={ariaLabel}
    >
      {/* Checkbox for selection - always render but hide in normal mode */}
      {isLoggedIn && selectionContext && (
        <BookmarkCheckbox
          bookmarkId={id}
          bookmarkTitle={title}
          bookmarkUrl={url}
          isSelected={isSelected}
          isBatchMode={isBatchMode}
          selectionContext={selectionContext}
          className={clsx(isBatchMode && 'batch-mode', condensedView && 'condensed')}
        />
      )}
      <div
        className={clsx(
          'bookmark-content',
          isBatchMode && 'batch-mode',
          condensedView && 'condensed',
          profileMode && 'profile-mode',
        )}
      >
        {/* Main content row with meta, description, notes/tags and thumbnail */}
        <div className="bookmark-content-body">
          <div className="bookmark-row-header">
            <div className="bookmark-row-header-main">
              <BookmarkTitle
                bookmarkId={id}
                title={title}
                url={url}
                isLoggedIn={isLoggedIn}
                condensedView={condensedView}
                profileMode={profileMode}
                isBatchMode={isBatchMode}
              />
              <BookmarkMeta
                domain={source}
                url={url}
                readTimeText={readTimeText}
                time={time}
                author={author}
                hideAuthor={bookmarkPage?.readerLayout === '3col'}
              />
            </div>
            {showActions && (
              <div className="bookmark-actions">
                <BookmarkActions
                  key={actionsAnchorPosition ? 'virtual' : 'trigger'}
                  bookmark={bookmark}
                  viewMode={viewMode}
                  currentFolderId={currentFolderId}
                  open={actionsOpen}
                  onOpenChange={handleActionsOpenChange}
                  anchorPosition={actionsAnchorPosition}
                />
              </div>
            )}
          </div>
          <BookmarkDescription description={description} condensedView={condensedView} />
          <BookmarkNotesTags notes={notes} tags={tags} bookmarkId={id} />
        </div>
        {/* Thumbnail - all the way to the right */}
        <BookmarkThumbnail
          thumbnailUrl={thumbnailUrl}
          showThumbnails={showThumbnails}
          condensedView={condensedView}
        />
      </div>
    </label>
  );
}
export default BookmarkRow;
